import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X, GraduationCap, ArrowRight } from 'lucide-react';
import Button from '../Button';
import Hyperlink from '../Hyperlink';
import Input from '../Input';
import useSwipeToClose from '../hooks/useSwipeToClose';
import './SchoolSelector.css';
import { MOBILE_QUERY, DESKTOP_QUERY } from '../utils/breakpoints';
import { drawerDurationMs } from '../utils/cssDuration';

/* Custom clock icon from Figma (node 7854:124693) */
const SchoolDatesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <g clipPath="url(#school-dates-clip)">
      <path opacity="0.1" d="M16 31.5C24.5604 31.5 31.5 24.5604 31.5 16C31.5 7.43959 24.5604 0.5 16 0.5C7.43959 0.5 0.5 7.43959 0.5 16C0.5 24.5604 7.43959 31.5 16 31.5Z" fill="currentColor"/>
      <path d="M16 1C18.9667 1 21.8668 1.87973 24.3336 3.52796C26.8003 5.17618 28.7229 7.51886 29.8582 10.2597C30.9935 13.0006 31.2906 16.0166 30.7118 18.9264C30.133 21.8361 28.7044 24.5088 26.6066 26.6066C24.5088 28.7044 21.8361 30.133 18.9264 30.7118C16.0166 31.2906 13.0006 30.9935 10.2598 29.8582C7.51886 28.7229 5.17618 26.8003 3.52796 24.3336C1.87974 21.8668 1.00001 18.9667 1.00001 16C1.00001 12.0218 2.58036 8.20644 5.39341 5.3934C8.20645 2.58035 12.0218 1 16 1ZM16 0C12.8355 0 9.74207 0.938384 7.11088 2.69649C4.4797 4.45459 2.42894 6.95345 1.21793 9.87706C0.0069325 12.8007 -0.309921 16.0177 0.307443 19.1214C0.924806 22.2251 2.44866 25.0761 4.6863 27.3137C6.92394 29.5513 9.77487 31.0752 12.8786 31.6926C15.9823 32.3099 19.1993 31.9931 22.1229 30.7821C25.0466 29.5711 27.5454 27.5203 29.3035 24.8891C31.0616 22.2579 32 19.1645 32 16C32 11.7565 30.3143 7.68687 27.3137 4.68629C24.3131 1.68571 20.2435 0 16 0V0Z" fill="currentColor"/>
      <path d="M16 2C18.7689 2 21.4757 2.82109 23.778 4.35943C26.0803 5.89777 27.8747 8.08427 28.9343 10.6424C29.9939 13.2006 30.2712 16.0155 29.731 18.7313C29.1908 21.447 27.8574 23.9416 25.8995 25.8995C23.9416 27.8574 21.447 29.1908 18.7313 29.731C16.0155 30.2712 13.2006 29.9939 10.6424 28.9343C8.08427 27.8747 5.89777 26.0803 4.35943 23.778C2.82109 21.4757 2.00001 18.7689 2.00001 16C2.00001 12.287 3.475 8.72601 6.10051 6.1005C8.72602 3.475 12.287 2 16 2ZM16 1C13.0333 1 10.1332 1.87973 7.66645 3.52796C5.19972 5.17618 3.27713 7.51886 2.14181 10.2597C1.0065 13.0006 0.709449 16.0166 1.28823 18.9264C1.86701 21.8361 3.29562 24.5088 5.3934 26.6066C7.49119 28.7044 10.1639 30.133 13.0737 30.7118C15.9834 31.2906 18.9994 30.9935 21.7403 29.8582C24.4812 28.7229 26.8238 26.8003 28.4721 24.3336C30.1203 21.8668 31 18.9667 31 16C31 12.0218 29.4197 8.20644 26.6066 5.3934C23.7936 2.58035 19.9783 1 16 1Z" fill="white"/>
      <path d="M8.6001 8.83008L16.0001 16.2301" stroke="currentColor" strokeMiterlimit="10" strokeLinecap="round"/>
      <path d="M16.0098 3.5C16.087 3.5 16.1502 3.56249 16.1504 3.63965C16.1504 3.71697 16.0871 3.78027 16.0098 3.78027C15.9326 3.78008 15.8701 3.71685 15.8701 3.63965C15.8703 3.56261 15.9327 3.50019 16.0098 3.5Z" fill="currentColor" stroke="currentColor"/>
      <path d="M16.0098 28.2207C16.087 28.2207 16.1502 28.2832 16.1504 28.3604C16.1504 28.4377 16.0871 28.501 16.0098 28.501C15.9326 28.5008 15.8701 28.4376 15.8701 28.3604C15.8703 28.2833 15.9327 28.2209 16.0098 28.2207Z" fill="currentColor" stroke="currentColor"/>
      <path d="M28.3596 16.0098C28.4368 16.0098 28.5001 16.0723 28.5002 16.1494C28.5002 16.2267 28.4369 16.29 28.3596 16.29C28.2825 16.2898 28.22 16.2266 28.22 16.1494C28.2202 16.0724 28.2826 16.01 28.3596 16.0098Z" fill="currentColor" stroke="currentColor"/>
      <path d="M3.63965 16.0098C3.71685 16.0098 3.78008 16.0723 3.78027 16.1494C3.78027 16.2267 3.71697 16.29 3.63965 16.29C3.56249 16.2898 3.5 16.2266 3.5 16.1494C3.50019 16.0724 3.56261 16.01 3.63965 16.0098Z" fill="currentColor" stroke="currentColor"/>
      <path d="M15.8479 15.2354C15.9991 15.2053 16.1557 15.2213 16.2981 15.2803C16.4406 15.3393 16.563 15.4391 16.6487 15.5674C16.7343 15.6956 16.7795 15.8467 16.7795 16.001C16.7795 16.2077 16.6982 16.4065 16.552 16.5527C16.4058 16.6989 16.207 16.7802 16.0002 16.7803C15.846 16.7803 15.6949 16.7351 15.5667 16.6494C15.4384 16.5637 15.3386 16.4414 15.2795 16.2988C15.2206 16.1564 15.2046 15.9998 15.2346 15.8486C15.2647 15.6973 15.3394 15.5583 15.4485 15.4492C15.5576 15.3401 15.6966 15.2654 15.8479 15.2354Z" fill="white" stroke="currentColor"/>
    </g>
    <defs>
      <clipPath id="school-dates-clip">
        <rect width="32" height="32" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

/**
 * SchoolSelector Component
 * 
 * Desktop: modal popup with banner image + form
 * Mobile: bottom sheet drawer with swipe-to-dismiss
 * 
 * Two-step flow:
 *   1. School Selection — searchable dropdown (DS Input type="searchable")
 *   2. Class Selection — dropdown (DS Input type="dropdown")
 * 
 * Setting `requireClass={false}` skips class selection.
 *
 * Figma nodes:
 * - Desktop School Selection: 3639:14235
 * - Desktop Class Selection:  3639:14237
 * - Mobile Default:           3643:12850
 */

/* Sentinel value for the opt-in "my school is not in the list" option. */
const NOT_IN_LIST_VALUE = '__school_not_in_list__';

const SchoolSelector = ({
  isOpen = false,
  onClose,
  onSelect,
  schools = [],
  requireClass = true,
  requireSeason = false,
  title = 'Vælg din skole',
  infoText = 'Vælg din skole for at få adgang til unikke priser, produkter og levering til skolen',
  schoolPlaceholder = 'Type your school name',
  seasonPlaceholder = 'Select season',
  classPlaceholder = 'Select class',
  submitLabel = 'Continue',
  notFoundTitle = "Can't find your school?",
  notFoundLinkLabel = 'OPRET KLASSESHOP',
  notFoundLinkHref = '#',
  onNotFoundClick = null,
  // Opt-in escape hatch: when `notInListOption` is set, a "my school is not in
  // the list" row is offered in the school dropdown whenever the search matches
  // no school. Picking it swaps the submit button (and the whole bottom area)
  // for a title + CTA. Off by default — brands that don't pass it are unchanged.
  notInListOption = null,
  notInListTitle = '',
  notInListCtaLabel = '',
  onNotInListCtaClick = null,
  schoolInfo = null,
  getSchoolInfo = null,
  onClear = null,
  clearLabel = 'Remove school selection',
  initialSchool = null,
  initialSeason = null,
  initialClass = null,
  // When set, called with the picked school; returning one of its seasons
  // auto-selects it and skips the season dropdown (like the single-season
  // case). Used by menu-item intents to jump straight to the mapped shop.
  preferredSeasonResolver = null,
  // true = a preferred season is only PRESELECTED: the season dropdown stays
  // visible (2+ seasons) so the customer sees — and may change — the shop.
  showPreferredSeason = false,
  banner = null,
  preview = false,
  lockScroll = true,
  // false = the popup cannot be dismissed (no X, overlay/ESC/swipe ignored):
  // the customer must complete the selection (or take an explicit exit like
  // the create-school / book-meeting CTAs). Used for mandatory identification
  // before add-to-cart on CLS-DK product pages.
  dismissible: dismissibleProp = true,
  noClassesText = 'No classes are open for this shop yet — please contact us and we will help you.',
  className = '',
}) => {
  /* ── State ── */
  const [step, setStep] = useState('school');            // 'school' | 'season' | 'class'
  const [searchSchoolValue, setSearchSchoolValue] = useState('');
  const [searchSeasonValue, setSearchSeasonValue] = useState('');
  const [searchClassValue, setSearchClassValue] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  // True when preferredSeasonResolver picked the season — suppresses the
  // season dropdown even for schools with 2+ seasons.
  const [autoPickedSeason, setAutoPickedSeason] = useState(false);
  // True once the customer picked the "not in the list" escape hatch.
  const [notInList, setNotInList] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isEntered, setIsEntered] = useState(false);

  // Resolve the preferred season for a school, constrained to the school's
  // own seasons list (by identity or id) so downstream state stays coherent.
  const resolvePreferredSeason = useCallback((school) => {
    if (!preferredSeasonResolver || !school) return null;
    const seasons = school.seasons || [];
    const preferred = preferredSeasonResolver(school);
    if (!preferred) return null;
    return seasons.find((s) => s === preferred || String(s.id) === String(preferred.id)) ?? null;
  }, [preferredSeasonResolver]);

  const overlayRef = useRef(null);
  const schoolInputRef = useRef(null);
  const enteredTimer = useRef(null);
  const lockedScrollY = useRef(0);
  const bodyPinned = useRef(false);
  const unpinBody = () => {
    if (!bodyPinned.current) return;
    bodyPinned.current = false;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo({ top: lockedScrollY.current, behavior: 'instant' });
  };
  const closeTimer = useRef(null);

  /* ── Derived flags ── */
  // A season is required as soon as the picked school has at least one open
  // season (we either auto-pick it or let the user choose). When the school
  // has none, we fall back to the school-level classes.
  const schoolSeasons = selectedSchool?.seasons || [];
  const seasonsNeeded = requireSeason && schoolSeasons.length > 0;
  // Only render the season dropdown when there's a real choice (2+ seasons).
  // A single season is auto-selected and the dropdown is skipped — as is a
  // season auto-picked by preferredSeasonResolver (menu-item intent).
  const showSeasonPicker = requireSeason && schoolSeasons.length > 1 && (!autoPickedSeason || showPreferredSeason);

  /* ── Options for DS Input ── */
  // The escape hatch is appended as a `fallback` option — the DS Input keeps it
  // out of the normal list and shows it only when the search matched nothing.
  // Only offered once real schools have loaded, so it never appears alone.
  const schoolOptions = [
    ...schools.map((s) => ({ value: s.id, label: s.name })),
    ...(notInListOption && schools.length > 0
      ? [{ value: NOT_IN_LIST_VALUE, label: notInListOption.label, fallback: true }]
      : []),
  ];
  const seasonOptions = schoolSeasons.map((s) => ({ value: s.id, label: s.name }));
  // Classes come from the chosen shop; a shop with none configured falls back
  // to the school's own list so the customer is never handed an empty dropdown.
  const classSource = (selectedSeason?.classes || []).length
    ? selectedSeason.classes
    : (selectedSchool?.classes || []);
  const classOptions = classSource.map((cls) => ({ value: cls, label: cls }));
  // Nothing to choose from anywhere: a mandatory popup must not soft-lock the
  // customer, so a class-less dead end is always closable and says why.
  const noClassDeadEnd = requireClass && step === 'class' && !!selectedSchool && classOptions.length === 0;
  const dismissible = dismissibleProp || noClassDeadEnd;

  /* ── Reset internal state when opened ── */
  useEffect(() => {
    if (isOpen) {
      const restoredSchool = initialSchool
        ? schools.find((s) => s.id === initialSchool.id) ?? null
        : null;
      const restoredSchoolSeasons = restoredSchool?.seasons || [];
      const restoredSeasonsNeeded = requireSeason && restoredSchoolSeasons.length > 0;
      // Preferred season (menu-item intent) wins; then the single available
      // season; otherwise honour initialSeason.
      let restoredSeason = null;
      let restoredAutoPicked = false;
      if (restoredSeasonsNeeded) {
        const preferred = resolvePreferredSeason(restoredSchool);
        if (preferred) {
          restoredSeason = preferred;
          restoredAutoPicked = true;
        } else if (restoredSchoolSeasons.length === 1) {
          restoredSeason = restoredSchoolSeasons[0];
        } else if (initialSeason) {
          restoredSeason = restoredSchoolSeasons.find((s) => s.id === initialSeason.id) ?? null;
        }
      }
      const restoredShowSeasonPicker = requireSeason && restoredSchoolSeasons.length > 1 && (!restoredAutoPicked || showPreferredSeason);
      const restoredClassSource = (restoredSeason?.classes || []).length
        ? restoredSeason.classes
        : (restoredSchool?.classes || []);
      const restoredClass = restoredSchool && initialClass
        ? (restoredClassSource.includes(initialClass) ? initialClass : null)
        : null;

      setSelectedSchool(restoredSchool);
      setSearchSchoolValue(restoredSchool?.name ?? '');
      setSelectedSeason(restoredSeason);
      setSearchSeasonValue(restoredSeason?.name ?? '');
      setAutoPickedSeason(restoredAutoPicked);
      setSelectedClass(restoredClass);
      setSearchClassValue(restoredClass ?? '');
      setNotInList(false);

      // Choose the right next step based on what's already restored.
      let initialStep = 'school';
      if (restoredSchool) {
        if (restoredShowSeasonPicker && !restoredSeason) initialStep = 'season';
        else if (requireClass) initialStep = 'class';
      }
      setStep(initialStep);
      setIsClosing(false);
      setIsEntered(preview);

      if (!preview && lockScroll) {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        // iOS Safari keeps touch-scrolling the page behind an overflow:hidden body;
        // pinning the body is the only reliable lock. Restored (with the scroll
        // position) when the drawer closes.
        if (window.matchMedia(MOBILE_QUERY).matches && document.body.style.position !== 'fixed') {
          lockedScrollY.current = window.scrollY;
          document.body.style.position = 'fixed';
          document.body.style.top = `-${lockedScrollY.current}px`;
          document.body.style.left = '0';
          document.body.style.right = '0';
          document.body.style.width = '100%';
          bodyPinned.current = true;
        }
      }

      if (!preview) {
        enteredTimer.current = setTimeout(() => setIsEntered(true), drawerDurationMs() + 50);
        // Auto-focus school input (desktop only — mobile opens dropdown without keyboard)
        const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
        if (isDesktop) {
          setTimeout(() => schoolInputRef.current?.focus(), 300);
        }
      }
    } else if (!preview && lockScroll) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      unpinBody();
    }
    return () => {
      if (!preview && lockScroll) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        unpinBody();
      }
      if (enteredTimer.current) clearTimeout(enteredTimer.current);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [isOpen, preview, lockScroll, initialSchool, initialSeason, initialClass, requireClass, requireSeason, schools, resolvePreferredSeason, showPreferredSeason]);

  /* ── Close helpers ── */
  const animateClose = useCallback(() => {
    setIsClosing(true);
    setIsEntered(false);
    closeTimer.current = setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, drawerDurationMs());
  }, [onClose]);

  // DISMISSAL paths only (X, overlay, ESC, swipe) — a completed selection and
  // the explicit CTAs always close via animateClose directly.
  const dismissClose = useCallback(() => {
    if (!dismissible) return;
    animateClose();
  }, [dismissible, animateClose]);

  /* ── Mobile swipe-to-close (useSwipeToClose hook) ── */
  const {
    containerRef: mobileDrawerRef,
    dragOffset,
    isDragging,
    DragHandle,
  } = useSwipeToClose(dismissClose, isOpen && !preview && dismissible);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) dismissClose();
  };

  /* ── School selection (via DS Input onSelect) ── */
  const handleSchoolInputChange = (e) => {
    const val = e.target.value;
    setSearchSchoolValue(val);
    // Typing again leaves the "not in the list" state.
    if (notInList) setNotInList(false);
    // If user is actively editing, reset selection
    if (selectedSchool) {
      setSelectedSchool(null);
      setSelectedSeason(null);
      setSearchSeasonValue('');
      setSearchClassValue('');
      setSelectedClass(null);
      setAutoPickedSeason(false);
      setStep('school');
    }
  };

  const handleSchoolInputSelect = (option) => {
    if (!option) {
      // Cleared via Input's X button
      setSelectedSchool(null);
      setSearchSchoolValue('');
      setSelectedSeason(null);
      setSearchSeasonValue('');
      setSearchClassValue('');
      setSelectedClass(null);
      setAutoPickedSeason(false);
      setNotInList(false);
      setStep('school');
      const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
      if (isDesktop) {
        setTimeout(() => schoolInputRef.current?.focus(), 50);
      }
      return;
    }
    if (option.value === NOT_IN_LIST_VALUE) {
      // Escape hatch: no school to select — clear everything downstream and
      // swap the submit button for the "book a meeting" CTA.
      setNotInList(true);
      setSearchSchoolValue(option.label);
      setSelectedSchool(null);
      setSelectedSeason(null);
      setSearchSeasonValue('');
      setSelectedClass(null);
      setSearchClassValue('');
      setAutoPickedSeason(false);
      setStep('school');
      return;
    }
    setNotInList(false);
    const school = schools.find((s) => s.id === option.value);
    if (school) {
      setSelectedSchool(school);
      setSearchSchoolValue(school.name);
      // Reset downstream state — new school invalidates any prior season/class.
      setSelectedClass(null);
      setSearchClassValue('');
      const seasonsForSchool = school.seasons || [];
      const preferred = requireSeason && seasonsForSchool.length > 0
        ? resolvePreferredSeason(school)
        : null;
      if (preferred) {
        // Menu-item intent resolved a mapped season — auto-select it and
        // jump straight to class (the season dropdown stays hidden).
        setSelectedSeason(preferred);
        setSearchSeasonValue(preferred.name);
        setAutoPickedSeason(true);
        if (requireClass) setStep('class');
      } else if (requireSeason && seasonsForSchool.length === 1) {
        // Exactly one open season — auto-select and jump straight to class.
        const onlySeason = seasonsForSchool[0];
        setSelectedSeason(onlySeason);
        setSearchSeasonValue(onlySeason.name);
        setAutoPickedSeason(false);
        if (requireClass) setStep('class');
      } else {
        setSelectedSeason(null);
        setSearchSeasonValue('');
        setAutoPickedSeason(false);
        if (requireSeason && seasonsForSchool.length > 1) {
          setStep('season');
        } else if (requireClass) {
          setStep('class');
        }
      }
    }
  };

  /* ── Season selection (via DS Input onSelect) ── */
  const handleSeasonInputChange = (e) => {
    const val = e.target.value;
    setSearchSeasonValue(val);
    if (selectedSeason) {
      setSelectedSeason(null);
      setSelectedClass(null);
      setSearchClassValue('');
    }
  };

  const handleSeasonInputSelect = (option) => {
    if (!option) {
      setSelectedSeason(null);
      setSearchSeasonValue('');
      setSelectedClass(null);
      setSearchClassValue('');
      return;
    }
    const season = schoolSeasons.find((s) => s.id === option.value);
    if (season) {
      setSelectedSeason(season);
      setSearchSeasonValue(season.name);
      setSelectedClass(null);
      setSearchClassValue('');
      if (requireClass) {
        setStep('class');
      }
    }
  };

  /* ── Class selection (via DS Input onSelect) ── */
  const handleClassInputChange = (e) => {
    const val = e.target.value;
    setSearchClassValue(val);
    if (selectedClass) {
      setSelectedClass(null);
    }
  };

  const handleClassInputSelect = (option) => {
    if (!option) {
      setSelectedClass(null);
      setSearchClassValue('');
      return;
    }
    setSelectedClass(option.value);
    setSearchClassValue(option.value);
  };

  /* ── Submit ── */
  const handleSubmit = () => {
    if (!selectedSchool) return;
    if (seasonsNeeded && !selectedSeason) return;
    if (requireClass && !selectedClass) return;
    onSelect?.({
      school: selectedSchool,
      season: selectedSeason,
      class: selectedClass,
    });
    animateClose();
  };

  const handleClear = () => {
    setSelectedSchool(null);
    setSearchSchoolValue('');
    setSelectedSeason(null);
    setSearchSeasonValue('');
    setSelectedClass(null);
    setSearchClassValue('');
    setAutoPickedSeason(false);
    setStep('school');
    onClear?.();
    // Mandatory mode: clearing resets the form but keeps the popup open —
    // the customer still has to complete a selection.
    if (dismissible) animateClose();
  };



  /* ── Close on ESC ── */
  useEffect(() => {
    if (preview) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && isOpen) dismissClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, dismissClose, preview]);

  if (!isOpen && !isClosing) return null;

  // Only render the season dropdown when the user actually has a choice
  // (2+ open seasons). One season is auto-selected silently.
  const isSeasonStep = showSeasonPicker && (step === 'season' || step === 'class') && selectedSchool;
  const isClassStep = step === 'class' && requireClass && selectedSchool && (!seasonsNeeded || selectedSeason);
  const resolvedSchoolInfo = getSchoolInfo ? getSchoolInfo(selectedSchool) : schoolInfo;

  // Bottom area: school dates once a full selection is in place, otherwise the
  // "can't find your school" strip. In the "not in the list" state neither is
  // shown — the CTA that replaces the submit button is the only action left.
  const showDatesBottom = !notInList
    && !!resolvedSchoolInfo
    && !!selectedSchool
    && (!seasonsNeeded || !!selectedSeason)
    && (!requireClass || !!selectedClass);
  const showNotFoundBottom = !notInList && !showDatesBottom;

  const hadSchool = !!initialSchool;
  const hasSchool = !!selectedSchool;

  // Red: had a school, now cleared to no school
  const isClearMode = onClear && hadSchool && !hasSchool;
  // Green: a complete selection is in place — submit-and-forward is always
  // valid, even when nothing changed since the popup was last closed (lets
  // the user re-open and click straight through to the shop).
  const isSubmitReady = hasSchool
    && (!seasonsNeeded || !!selectedSeason)
    && (!requireClass || !!selectedClass);
  // Gray/disabled: incomplete selection
  const isDisabled = !isClearMode && !isSubmitReady;

  const drawerStyle = dragOffset > 0 || isDragging
    ? {
        transform: `translateY(${dragOffset}px)`,
        transition: isDragging ? 'none' : 'transform var(--transition-time-drawer) var(--transition-function-drawer)',
      }
    : {};

  /* ── Content shared between desktop & mobile ── */
  const renderContent = () => (
    <>
      <div className="ds-school-selector__container">
        {/* Close button — hidden in mandatory (non-dismissible) mode */}
        {dismissible && (
        <div className="ds-school-selector__close">
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={<X size={20} />}
            onClick={dismissClose}
            aria-label="Close"
          />
        </div>
        )}

        <div className="ds-school-selector__wrapper">
          {/* Top: title + info */}
          <div className={`ds-school-selector__top ${isClassStep ? 'ds-school-selector__top--class' : ''}`}>
            <h3 className="ds-school-selector__title typo-h3">{title}</h3>
            <div className="ds-school-selector__info">
              <p className="ds-school-selector__info-text typo-body">{infoText}</p>
            </div>
          </div>

          {/* Form */}
          <div className="ds-school-selector__form">
            <div className="ds-school-selector__inputs">
              {/* School — DS Input searchable dropdown */}
              <Input
                ref={schoolInputRef}
                type="searchable"
                label={schoolPlaceholder}
                value={searchSchoolValue}
                onChange={handleSchoolInputChange}
                options={schoolOptions}
                onSelect={handleSchoolInputSelect}
                showOptionIcons={false}
                mobileFocus
                className="ds-school-selector__school-input"
              />

              {/* Season — DS Input dropdown (only when caller opts in and the
                  picked school actually has open seasons). */}
              {isSeasonStep && (
                <Input
                  type="searchable"
                  label={seasonPlaceholder}
                  value={searchSeasonValue}
                  onChange={handleSeasonInputChange}
                  options={seasonOptions}
                  onSelect={handleSeasonInputSelect}
                  showOptionIcons={false}
                  mobileFocus
                  className="ds-school-selector__season-input"
                />
              )}

              {/* Class — DS Input dropdown (only when school selected & class
                  required, and — if seasons are in play — a season is picked). */}
              {isClassStep && (
                <Input
                  type="searchable"
                  label={classPlaceholder}
                  icon={<GraduationCap size={20} />}
                  value={searchClassValue}
                  onChange={handleClassInputChange}
                  options={classOptions}
                  onSelect={handleClassInputSelect}
                  showOptionIcons={false}
                  mobileFocus
                  className="ds-school-selector__class-input"
                />
              )}
              {noClassDeadEnd && (
                <p className="ds-school-selector__no-classes typo-body-sm" role="status">{noClassesText}</p>
              )}
            </div>

            {/* "Not in the list" replaces the submit button with a CTA that
                hands the customer over to the caller (e.g. a booking form). */}
            {notInList ? (
              <div className="ds-school-selector__not-in-list">
                <p className="ds-school-selector__not-in-list-title typo-h4">{notInListTitle}</p>
                <Button
                  size="primary"
                  variant="main"
                  iconRight={<ArrowRight size={20} />}
                  onClick={() => { animateClose(); onNotInListCtaClick?.(); }}
                  className="ds-school-selector__not-in-list-cta"
                >
                  {notInListCtaLabel}
                </Button>
              </div>
            ) : (
              <Button
                size="primary"
                variant={isClearMode ? 'danger' : 'main'}
                iconRight={isClearMode ? undefined : <ArrowRight size={20} />}
                onClick={isClearMode ? handleClear : handleSubmit}
                disabled={isDisabled}
                className="ds-school-selector__submit"
              >
                {isClearMode ? clearLabel : submitLabel}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Bottom area — school dates or "not found"; both hidden in the
          "not in the list" state (see showDatesBottom / showNotFoundBottom). */}
      {showDatesBottom && (
        <div className="ds-school-selector__bottom ds-school-selector__bottom--dates">
          <div className="ds-school-selector__bottom-content">
            <div className="ds-school-selector__dates">
              <div className="ds-school-selector__dates-icon">
                <SchoolDatesIcon />
              </div>
              <div className="ds-school-selector__dates-text">
                <p className="ds-school-selector__dates-label typo-h6">
                  {resolvedSchoolInfo.statusLabel}
                </p>
                <p className="ds-school-selector__dates-range typo-h4">
                  {resolvedSchoolInfo.dateRange}
                </p>
                {resolvedSchoolInfo.description && (
                  <p className="ds-school-selector__dates-desc typo-body-sm">
                    {resolvedSchoolInfo.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showNotFoundBottom && (
        <div className="ds-school-selector__bottom ds-school-selector__bottom--not-found">
          <div className="ds-school-selector__bottom-content">
            <p className="ds-school-selector__bottom-title typo-h4">{notFoundTitle}</p>
            <Hyperlink
              variant="label"
              href={notFoundLinkHref}
              showIconRight
              iconRight={<ArrowRight size={20} />}
              onClick={onNotFoundClick ? (e) => { e.preventDefault(); animateClose(); onNotFoundClick(); } : undefined}
            >
              {notFoundLinkLabel}
            </Hyperlink>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div
      ref={overlayRef}
      className={`ds-school-selector__overlay ${isClosing ? 'ds-school-selector__overlay--closing' : ''} ${className}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* ── Desktop: centered popup ── */}
      <div
        className={`ds-school-selector ds-school-selector--desktop ${isClosing ? 'ds-school-selector--closing' : ''} ${isEntered ? 'ds-school-selector--entered' : ''}`}
      >
        {/* Banner image */}
        {banner && (
          <div className="ds-school-selector__banner">
            <img
              src={banner}
              alt=""
              className="ds-school-selector__banner-img"
            />
          </div>
        )}

        <div className="ds-school-selector__content">
          {renderContent()}
        </div>
      </div>

      {/* ── Mobile: bottom sheet ── */}
      <div
        ref={mobileDrawerRef}
        className={`ds-school-selector ds-school-selector--mobile ${isClosing ? 'ds-school-selector--closing' : ''} ${isEntered ? 'ds-school-selector--entered' : ''}`}
        style={drawerStyle}
      >
        {/* Swipe handle (via useSwipeToClose hook) */}
        <DragHandle className="ds-school-selector__swipe-handle" />

        <div className="ds-school-selector__content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

SchoolSelector.propTypes = {
  /** Whether the selector modal/drawer is open */
  isOpen: PropTypes.bool,
  /** Callback when the selector is closed */
  onClose: PropTypes.func,
  /** Callback with { school, class } when user submits */
  onSelect: PropTypes.func,
  /** Array of school objects: { id, name, classes: string[] } */
  schools: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      classes: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  /** Whether class selection is required after school selection */
  requireClass: PropTypes.bool,
  /** Whether to insert a season step between school and class, using each
   *  school's `seasons` array. Falls back to school-level classes if the
   *  picked school has no seasons. */
  requireSeason: PropTypes.bool,
  /** false = mandatory mode: no X, overlay/ESC/swipe ignored; only a completed selection (or an explicit CTA) closes the popup. */
  dismissible: PropTypes.bool,
  noClassesText: PropTypes.string,
  /** (school) => season|null — a returned member of school.seasons is
   *  auto-selected and the season dropdown is skipped (menu-item intents). */
  preferredSeasonResolver: PropTypes.func,
  /** Keep the season dropdown visible when preferredSeasonResolver preselected one. */
  showPreferredSeason: PropTypes.bool,
  /** Modal title */
  title: PropTypes.string,
  /** Info text displayed below title */
  infoText: PropTypes.string,
  /** Placeholder for school search input */
  schoolPlaceholder: PropTypes.string,
  /** Placeholder for season dropdown (used when requireSeason is set) */
  seasonPlaceholder: PropTypes.string,
  /** Placeholder for class dropdown */
  classPlaceholder: PropTypes.string,
  /** Submit button label */
  submitLabel: PropTypes.string,
  /** Title in "school not found" bottom area */
  notFoundTitle: PropTypes.string,
  /** Link label in "school not found" area */
  notFoundLinkLabel: PropTypes.string,
  /** Link href for creating a class shop */
  notFoundLinkHref: PropTypes.string,
  /** Opt-in escape hatch shown when the school search matches nothing: { label } */
  notInListOption: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }),
  /** Copy shown once the escape hatch is picked */
  notInListTitle: PropTypes.string,
  /** CTA label shown once the escape hatch is picked */
  notInListCtaLabel: PropTypes.string,
  /** Fired after the popup closes when the escape-hatch CTA is clicked */
  onNotInListCtaClick: PropTypes.func,
  /** School info shown when school is selected: { statusLabel, dateRange, description } */
  schoolInfo: PropTypes.shape({
    statusLabel: PropTypes.string.isRequired,
    dateRange: PropTypes.string.isRequired,
    description: PropTypes.string,
  }),
  /** Callback (school) => schoolInfo, used to derive info per school dynamically */
  getSchoolInfo: PropTypes.func,
  /** Callback when user clears school selection */
  onClear: PropTypes.func,
  /** Label for the clear-selection link */
  clearLabel: PropTypes.string,
  /** Pre-select a school when opening: { id, name, classes, seasons } */
  initialSchool: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    classes: PropTypes.arrayOf(PropTypes.string),
  }),
  /** Pre-select a season when opening (requires initialSchool + requireSeason) */
  initialSeason: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  /** Pre-select a class when opening (requires initialSchool) */
  initialClass: PropTypes.string,
  /** Banner image source (desktop only) */
  banner: PropTypes.string,
  /** Static preview mode — disables scroll lock, auto-focus, ESC */
  preview: PropTypes.bool,
  /** Whether opening the selector locks page scroll */
  lockScroll: PropTypes.bool,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default SchoolSelector;
